import styled, { css, keyframes } from 'styled-components'
import { zIndexSystem } from '@/styles/theme'

export type SnackBarVariant = 'success' | 'warning' | 'critical' | 'loading'

export interface SnackBarItemData {
  id: string
  message: string
  variant: SnackBarVariant
  closing?: boolean
}

interface ItemProps extends SnackBarItemData {
  onClose: (id: string) => void
}

export function SnackBarListBox({ list, onClose }: { list: SnackBarItemData[]; onClose: (id: string) => void }) {
  if (!list.length) return null
  return (
    <Container>
      {list.map((item) => (
        <SnackBarItem key={item.id} {...item} onClose={onClose} />
      ))}
    </Container>
  )
}

function SnackBarItem({ id, message, variant, closing, onClose }: ItemProps) {
  return (
    <CollapseWrapper $closing={closing}>
      <Bar $closing={closing}>
        <Content>
          <IconWrap>
            {variant === 'success' && <CheckCircleIcon />}
            {variant === 'critical' && <CloseCircleIcon />}
            {variant === 'warning' && <AlertTriangleIcon />}
            {variant === 'loading' && <SpinnerRing />}
          </IconWrap>
          <Message>{message}</Message>
        </Content>
        <CloseBtn onClick={() => onClose(id)}>
          <CloseIcon />
        </CloseBtn>
      </Bar>
    </CollapseWrapper>
  )
}

function CheckCircleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" fill="#2fa84f" />
      <path d="M7 12.5l3.5 3.5 6.5-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function CloseCircleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" fill="#e02020" />
      <path d="M15 9l-6 6M9 9l6 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

function AlertTriangleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" fill="#fa9200" />
      <line x1="12" y1="9" x2="12" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="17" r="1" fill="white" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

const spin = keyframes`
  to { transform: rotate(360deg); }
`

const slideIn = keyframes`
  from { transform: translateX(calc(100% + 32px)); opacity: 0; }
  to   { transform: translateX(0); opacity: 1; }
`

const slideOut = keyframes`
  from { transform: translateX(0); opacity: 1; }
  to   { transform: translateX(calc(100% + 32px)); opacity: 0; }
`

const collapseOut = keyframes`
  from { max-height: 100px; margin-bottom: 12px; }
  to   { max-height: 0; margin-bottom: 0; }
`

const Container = styled.div`
  position: fixed;
  top: 108px;
  right: 32px;
  z-index: ${zIndexSystem.modal + 2};
  display: inline-flex;
  flex-direction: column;
`

const CollapseWrapper = styled.div<{ $closing?: boolean }>`
  overflow: hidden;
  margin-bottom: 12px;
  ${({ $closing }) =>
    $closing &&
    css`
      animation: ${collapseOut} 0.15s ease 0.2s both;
    `}
`

const Bar = styled.div<{ $closing?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 390px;
  min-width: 150px;
  padding: 16px 24px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 4px;
  animation: ${({ $closing }) =>
    $closing
      ? css`
          ${slideOut} 0.2s ease forwards
        `
      : css`
          ${slideIn} 0.25s ease
        `};
`

const Content = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  min-width: 0;
`

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
`

const SpinnerRing = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

const Message = styled.p`
  flex: 1;
  min-width: 0;
  font-family: 'Pretendard', sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.36;
  color: white;
  margin: 0;
  padding: 2px 0;
`

const CloseBtn = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`
